import { useCallback, useRef, useState } from "react";
import { hexToRgb } from "../lib/color-engine";
import { recolorMaskedRegion, type Point } from "../lib/wall-recolor";
import { computeDownscaledSize } from "../lib/canvas-utils";

const PREVIEW_MAX_DIMENSION = 900; // live-preview working resolution
const CLOSE_POINT_TOLERANCE_PX = 16; // tap-near-start-point-to-close radius

interface WallVisualizerProps {
  selectedHex: string | null;
}

/**
 * Manual lasso wall selection + live recolor preview.
 *
 * Scoped deliberately to manual selection for v1 (see REVISED_SPEC.md
 * 4c) — automatic wall segmentation is flagged there as a separate
 * milestone requiring a real trained model, not a detail to bolt onto
 * this component.
 *
 * Performance approach for cross-device parity:
 *  - Live preview recolors run on a downscaled working copy (capped at
 *    PREVIEW_MAX_DIMENSION) so dragging/re-picking colors stays smooth
 *    on a mid-range phone; recoloring at full camera resolution on every
 *    color change would visibly lag on weaker devices.
 *  - Full-resolution recolor only happens once, on explicit "Apply" —
 *    see handleApply — not on every preview update.
 */
export function WallVisualizer({ selectedHex }: WallVisualizerProps) {
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const originalImageRef = useRef<HTMLImageElement | null>(null);
  const baseImageDataRef = useRef<ImageData | null>(null); // unrecolored preview-res copy

  const [polygon, setPolygon] = useState<Point[]>([]);
  const [isClosed, setIsClosed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [applyState, setApplyState] = useState<"idle" | "working" | "done">("idle");
  const [resultUrl, setResultUrl] = useState<string | null>(null);

  const drawPreviewBase = useCallback(() => {
    const canvas = previewCanvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || !baseImageDataRef.current) return;
    ctx.putImageData(baseImageDataRef.current, 0, 0);
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const img = new Image();
      img.onload = () => {
        const canvas = previewCanvasRef.current;
        if (!canvas) return;

        const { width, height } = computeDownscaledSize(
          img.naturalWidth,
          img.naturalHeight,
          PREVIEW_MAX_DIMENSION
        );
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, width, height);
        baseImageDataRef.current = ctx.getImageData(0, 0, width, height);

        originalImageRef.current = img;
        setPolygon([]);
        setIsClosed(false);
        setImageLoaded(true);
        setApplyState("idle");
        setResultUrl(null);
      };
      img.src = URL.createObjectURL(file);
    },
    []
  );

  const redrawPolygonOverlay = useCallback((points: Point[], closed: boolean) => {
    const canvas = previewCanvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || points.length === 0) return;

    ctx.strokeStyle = "#52606D";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    points.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
    if (closed) ctx.closePath();
    ctx.stroke();

    points.forEach((p, i) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, i === 0 ? 6 : 4, 0, Math.PI * 2);
      ctx.fillStyle = i === 0 ? "#52606D" : "#F7F6F3";
      ctx.strokeStyle = "#52606D";
      ctx.fill();
      ctx.stroke();
    });
  }, []);

  const applyRecolorToPreview = useCallback(() => {
    if (!selectedHex || !isClosed || polygon.length < 3 || !baseImageDataRef.current) {
      return;
    }
    const canvas = previewCanvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    // Work on a copy of the untouched base so re-picking a color doesn't
    // compound blends from the previous selection.
    const working = new ImageData(
      new Uint8ClampedArray(baseImageDataRef.current.data),
      baseImageDataRef.current.width,
      baseImageDataRef.current.height
    );
    recolorMaskedRegion(working, polygon, hexToRgb(selectedHex));
    ctx.putImageData(working, 0, 0);
    redrawPolygonOverlay(polygon, true);
  }, [selectedHex, isClosed, polygon, redrawPolygonOverlay]);

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!imageLoaded || isClosed) return;
      const canvas = previewCanvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const point: Point = {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };

      // Tapping near the start point closes the shape, same gesture a
      // physical lasso tool uses.
      if (polygon.length >= 3) {
        const start = polygon[0];
        const dist = Math.hypot(point.x - start.x, point.y - start.y);
        if (dist <= CLOSE_POINT_TOLERANCE_PX) {
          setIsClosed(true);
          drawPreviewBase();
          redrawPolygonOverlay(polygon, true);
          return;
        }
      }

      const next = [...polygon, point];
      setPolygon(next);
      drawPreviewBase();
      redrawPolygonOverlay(next, false);
    },
    [imageLoaded, isClosed, polygon, drawPreviewBase, redrawPolygonOverlay]
  );

  const handleReset = useCallback(() => {
    setPolygon([]);
    setIsClosed(false);
    setApplyState("idle");
    setResultUrl(null);
    drawPreviewBase();
  }, [drawPreviewBase]);

  const handleApply = useCallback(() => {
    const img = originalImageRef.current;
    if (!img || !selectedHex || !isClosed || polygon.length < 3) return;

    setApplyState("working");

    // Full-resolution render happens once, on demand — not on every
    // preview tick. Scale the (preview-resolution) polygon points up to
    // the image's natural resolution before recoloring.
    const scaleX = img.naturalWidth / (previewCanvasRef.current?.width ?? 1);
    const scaleY = img.naturalHeight / (previewCanvasRef.current?.height ?? 1);
    const fullResPolygon = polygon.map((p) => ({ x: p.x * scaleX, y: p.y * scaleY }));

    // Deferred to a microtask so the "working" state actually paints
    // before the (potentially heavy) full-res pixel loop runs.
    setTimeout(() => {
      const fullCanvas = document.createElement("canvas");
      fullCanvas.width = img.naturalWidth;
      fullCanvas.height = img.naturalHeight;
      const ctx = fullCanvas.getContext("2d");
      if (!ctx) {
        setApplyState("idle");
        return;
      }
      ctx.drawImage(img, 0, 0);
      const fullImageData = ctx.getImageData(0, 0, fullCanvas.width, fullCanvas.height);
      recolorMaskedRegion(fullImageData, fullResPolygon, hexToRgb(selectedHex));
      ctx.putImageData(fullImageData, 0, 0);

      fullCanvas.toBlob((blob) => {
        if (blob) setResultUrl(URL.createObjectURL(blob));
        setApplyState("done");
      }, "image/jpeg", 0.9);
    }, 0);
  }, [selectedHex, isClosed, polygon]);

  return (
    <div className="flex flex-col gap-4">
      <label
        className={[
          "flex min-h-[44px] w-fit cursor-pointer items-center rounded-md",
          "border border-[#D9D5CC] bg-[#F7F6F3] px-4 text-sm font-medium text-[#232220]",
          "hover:bg-[#EDEBE6]",
        ].join(" ")}
      >
        {imageLoaded ? "Choose a different photo" : "Upload a room photo"}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFileChange}
        />
      </label>

      {imageLoaded && !isClosed && (
        <p className="text-sm text-[#6B6660]">
          Tap around the wall to trace it. Tap the first point again to close
          the shape.
        </p>
      )}

      <canvas
        ref={previewCanvasRef}
        onClick={handleCanvasClick}
        className={[
          "w-full max-w-lg rounded-md border border-[#D9D5CC]",
          imageLoaded ? (isClosed ? "cursor-default" : "cursor-crosshair") : "hidden",
        ].join(" ")}
      />

      {isClosed && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={applyRecolorToPreview}
            disabled={!selectedHex}
            className="min-h-[44px] rounded-md bg-[#52606D] px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            Preview color
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!selectedHex || applyState === "working"}
            className="min-h-[44px] rounded-md border border-[#52606D] px-4 text-sm font-medium text-[#52606D] disabled:opacity-40"
          >
            {applyState === "working" ? "Rendering full resolution…" : "Apply at full resolution"}
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="min-h-[44px] rounded-md px-4 text-sm font-medium text-[#6B6660] hover:bg-[#EDEBE6]"
          >
            Re-trace wall
          </button>
        </div>
      )}

      {!selectedHex && isClosed && (
        <p className="text-sm text-[#6B6660]">
          Pick a color from the catalog or scanner to preview it here.
        </p>
      )}

      {applyState === "done" && resultUrl && (
        <a
          href={resultUrl}
          download="neorise-recolored-wall.jpg"
          className="min-h-[44px] w-fit rounded-md bg-[#232220] px-4 py-2.5 text-sm font-medium text-white"
        >
          Download full-resolution result
        </a>
      )}
    </div>
  );
}
